/**
 * PresetDataBrowser (iter-51) — read-only "browse everything" panel on
 * the Preset Templates page. Users used to only see preset content
 * when they clicked "Load" from a book editor; now every preset is
 * inspectable inline, without leaving the library page.
 *
 * Groups: front-matter · back-matter · chapter · book-info.
 * Each row is a lightweight disclosure with the section slot key, an
 * optional subtitle / description, and the full content in a
 * preformatted block.
 */
import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FileText, User, Search, X } from "lucide-react";

const KIND_LABEL = {
  front:   "Front-matter",
  back:    "Back-matter",
  chapter: "Chapter templates",
};

export function PresetDataBrowser({ presets }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [query, setQuery] = useState("");
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // iter-52 filter: match against name, key, title, subtitle, description
  // and content. Case-insensitive substring. Book-info presets match on
  // their field values as well.
  const matches = (p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const hay = [
      p.name, p.key, p.title, p.subtitle, p.description, p.content,
    ].filter(Boolean).join(" ").toLowerCase();
    if (hay.includes(q)) return true;
    if (p.kind === "info") {
      for (const v of Object.values(p.fields || {})) {
        if ((v || "").toLowerCase().includes(q)) return true;
      }
    }
    return false;
  };

  const filtered = useMemo(
    () => (presets || []).filter(matches),
    [presets, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const byKind = useMemo(() => {
    const out = { front: [], back: [], chapter: [] };
    for (const p of filtered) {
      if (p.kind && out[p.kind]) out[p.kind].push(p);
    }
    return out;
  }, [filtered]);
  const infoPresets = useMemo(
    () => filtered.filter((p) => p.kind === "info"),
    [filtered]);

  const totalSection = byKind.front.length + byKind.back.length + byKind.chapter.length;
  const totalInfo = infoPresets.length;
  const totalAll = totalSection + totalInfo;
  const totalUnfiltered = (presets || []).length;

  return (
    <section className="border border-[color:var(--border)] bg-white p-4"
             data-testid="pt-preset-data-browser">
      <button type="button" onClick={()=>setOpen(!open)}
              className="w-full flex items-start justify-between gap-3 text-left"
              aria-expanded={open}
              data-testid="pt-preset-data-toggle">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Preset Data</p>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">
            Browse the actual content of every saved preset without loading it into a book.
            Click a heading below to reveal its stored copy — useful for spot-checking
            disclaimers, review requests, and boilerplate before publishing.
            {totalAll > 0 && <> <span className="text-[color:var(--primary)]">{totalAll} preset{totalAll === 1 ? "" : "s"} available.</span></>}
          </p>
        </div>
        <span className="mt-1 text-[color:var(--text-muted)]">
          {open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
        </span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <div className="relative" data-testid="pt-preset-search-wrap">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"/>
            <input
              value={query}
              onChange={(e)=>setQuery(e.target.value)}
              placeholder="Search presets by name, key, or content…"
              className="w-full border border-[color:var(--border)] pl-7 pr-8 py-1.5 text-xs focus:outline-none focus:border-[color:var(--primary)]"
              data-testid="pt-preset-search-input"
            />
            {query && (
              <button type="button" onClick={()=>setQuery("")}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"
                      data-testid="pt-preset-search-clear">
                <X className="w-3.5 h-3.5"/>
              </button>
            )}
          </div>
          {query && (
            <p className="text-[11px] text-[color:var(--text-muted)]" data-testid="pt-preset-search-summary">
              Showing <span className="text-[color:var(--primary)]">{totalAll}</span> of {totalUnfiltered} preset{totalUnfiltered === 1 ? "" : "s"} matching “{query}”.
            </p>
          )}
          {(["front", "back", "chapter"]).map((k) => (
            <div key={k} data-testid={`pt-preset-data-group-${k}`}>
              <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2 flex items-center gap-2">
                <FileText className="w-3 h-3"/> {KIND_LABEL[k]} · {byKind[k].length}
              </p>
              {byKind[k].length === 0 ? (
                <p className="text-[11px] italic text-[color:var(--text-muted)] pl-4">None saved yet.</p>
              ) : (
                <ul className="space-y-1">
                  {byKind[k].map((p) => {
                    const isOpen = !!expanded[p.id];
                    return (
                      <li key={p.id} className="border border-[color:var(--border)] bg-[color:var(--canvas)]/40"
                          data-testid={`pt-preset-data-item-${p.id}`}>
                        <button type="button" onClick={()=>toggle(p.id)}
                                aria-expanded={isOpen}
                                className="w-full text-left px-3 py-2 flex items-start justify-between gap-2"
                                data-testid={`pt-preset-data-item-${p.id}-toggle`}>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium flex items-center gap-1">
                              {isOpen
                                ? <ChevronDown className="w-3 h-3 shrink-0"/>
                                : <ChevronRight className="w-3 h-3 shrink-0"/>}
                              <span className="truncate">{p.name}</span>
                            </p>
                            <p className="text-[10px] text-[color:var(--text-muted)] pl-4 truncate">
                              {p.key && <span className="font-mono">{p.key}</span>}
                              {p.key && p.title && " · "}
                              {p.title}
                            </p>
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-3 pb-3 pl-6 space-y-1"
                               data-testid={`pt-preset-data-item-${p.id}-body`}>
                            {p.subtitle && (
                              <p className="text-[10px] italic text-[color:var(--text-muted)]">
                                Subtitle: {p.subtitle}
                              </p>
                            )}
                            {p.description && (
                              <p className="text-[10px] text-[color:var(--text-muted)]">
                                TOC description: {p.description}
                              </p>
                            )}
                            <pre className="whitespace-pre-wrap font-reading text-[11px] leading-relaxed bg-white border border-[color:var(--border)] p-2 max-h-72 overflow-auto"
                                 data-testid={`pt-preset-data-item-${p.id}-content`}>
                              {p.content || "(no content)"}
                            </pre>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}

          {/* Book-info presets — separate group since they're field maps, not prose. */}
          <div data-testid="pt-preset-data-group-info">
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2 flex items-center gap-2">
              <User className="w-3 h-3"/> Book-info presets · {totalInfo}
            </p>
            {totalInfo === 0 ? (
              <p className="text-[11px] italic text-[color:var(--text-muted)] pl-4">None saved yet.</p>
            ) : (
              <ul className="space-y-1">
                {(infoPresets || []).map((p) => {
                  const isOpen = !!expanded[p.id];
                  const fields = p.fields || {};
                  const entries = Object.entries(fields).filter(([, v]) => (v || "").trim());
                  return (
                    <li key={p.id} className="border border-[color:var(--border)] bg-[color:var(--canvas)]/40"
                        data-testid={`pt-preset-data-info-${p.id}`}>
                      <button type="button" onClick={()=>toggle(p.id)}
                              aria-expanded={isOpen}
                              className="w-full text-left px-3 py-2 flex items-start justify-between gap-2"
                              data-testid={`pt-preset-data-info-${p.id}-toggle`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium flex items-center gap-1">
                            {isOpen
                              ? <ChevronDown className="w-3 h-3 shrink-0"/>
                              : <ChevronRight className="w-3 h-3 shrink-0"/>}
                            <span className="truncate">{p.name}</span>
                          </p>
                          <p className="text-[10px] text-[color:var(--text-muted)] pl-4">
                            {entries.length} field{entries.length === 1 ? "" : "s"} saved
                          </p>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 pl-6"
                             data-testid={`pt-preset-data-info-${p.id}-body`}>
                          {entries.length === 0 ? (
                            <p className="text-[10px] italic text-[color:var(--text-muted)]">
                              No non-empty fields on this preset.
                            </p>
                          ) : (
                            <dl className="grid grid-cols-3 gap-x-2 gap-y-1 text-[11px]">
                              {entries.map(([k, v]) => (
                                <React.Fragment key={k}>
                                  <dt className="col-span-1 uppercase tracking-widest text-[10px] text-[color:var(--text-muted)]">{k}</dt>
                                  <dd className="col-span-2 whitespace-pre-wrap break-words">{v}</dd>
                                </React.Fragment>
                              ))}
                            </dl>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
